import React, {useEffect} from 'react';
import {
  ActivityIndicator,
  Alert,
  Modal,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {styles} from './styles';
import SearchHeader from '../../components/SearchHeader/SearchHeader';
import {stylesEquipmentMaintenance as ms} from './styles-equipment-maintenance';
import CheckBox from '../../components/CheckBox/CheckBox';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import EquipamentsService from '../../services/EquipamentsService/EquipamentsService';
import {Colors} from '../../constants/Colors';
import {NavigationProps} from '../../navigation/Routes';
import {useUserStore} from '../../store/UserStore';
import {
  IClientEquipment,
  IMaintenanceCatalog,
  IMaintenanceDefect,
  IMaintenanceRequest,
  IMaintenanceResponse,
} from '../../types/Equipaments';

const HAS_RESTRICTION = [
  {id: 1, description: 'Sim', value: true},
  {id: 2, description: 'Não', value: false},
];

// Equipamento só é selecionável se tiver GEBRA (EQUNR) válido.
const hasGebra = (eq: IClientEquipment) =>
  !!eq.EQUNR && String(eq.EQUNR).trim() !== '';

const EquipmentMaintenance = () => {
  const {goBack} = useNavigation<NavigationProps>();
  const {user} = useUserStore();

  const [loading, setLoading] = React.useState<boolean>(true);
  const [submiting, setSubmiting] = React.useState<boolean>(false);

  // catálogo
  const [defects, setDefects] = React.useState<IMaintenanceDefect[]>([]);
  const [triageItems, setTriageItems] = React.useState<number[]>([]);
  const [visitWindows, setVisitWindows] = React.useState<string[]>([]);

  // equipamentos do cliente
  const [equipments, setEquipments] = React.useState<IClientEquipment[]>([]);

  // seleção do formulário
  const [selectedGebra, setSelectedGebra] = React.useState<string>('');
  const [selectedItems, setSelectedItems] = React.useState<number[]>([]);
  const [visitWindow, setVisitWindow] = React.useState<string>('');
  const [hasRestriction, setHasRestriction] = React.useState<boolean | null>(null);
  const [restrictionDescription, setRestrictionDescription] = React.useState<string>('');
  const [referencePoint, setReferencePoint] = React.useState<string>('');

  // resultado (tela de sucesso)
  const [result, setResult] = React.useState<IMaintenanceResponse | null>(null);

  // defeito aguardando triagem (modal). null = fechado.
  const [triageDefect, setTriageDefect] = React.useState<IMaintenanceDefect | null>(null);

  const loadDomain = async () => {
    try {
      setLoading(true);
      const [catalogRes, equipmentRes] = await Promise.all([
        EquipamentsService.MaintenanceCatalog(),
        EquipamentsService.EquipamentsGet(user?.clientCode),
      ]);
      const catalog: IMaintenanceCatalog = catalogRes.data;
      setDefects(catalog?.defects ?? []);
      setTriageItems(catalog?.triage_items ?? []);
      setVisitWindows(catalog?.visit_windows ?? []);
      // EquipamentsGet retorna array; pode vir objeto com details quando vazio
      const list = Array.isArray(equipmentRes) ? equipmentRes : [];
      setEquipments(list as unknown as IClientEquipment[]);
    } catch (error) {
      console.error(error);
      Alert.alert('Erro', 'Não foi possível carregar os dados de manutenção.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDomain();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDefectToggle = (defect: IMaintenanceDefect) => {
    const isSelected = selectedItems.includes(defect.item);
    if (isSelected) {
      setSelectedItems(prev => prev.filter(i => i !== defect.item));
      return;
    }
    // Triagem 100% frontend: abre modal (funciona em mobile e web).
    if (triageItems.includes(defect.item)) {
      setTriageDefect(defect);
      return;
    }
    setSelectedItems(prev => [...prev, defect.item]);
  };

  // Voltou a funcionar -> encerra este defeito, não abre chamado.
  const handleTriageRecovered = () => setTriageDefect(null);

  // Continua com problema -> mantém o item no POST.
  const handleTriageNotRecovered = () => {
    if (triageDefect) {
      setSelectedItems(prev => [...prev, triageDefect.item]);
    }
    setTriageDefect(null);
  };

  const handleRestrictionSelection = (option: {value: boolean}) => {
    setRestrictionDescription('');
    setHasRestriction(prev => (prev === option.value ? null : option.value));
  };

  const validate = (): string | null => {
    if (!selectedGebra) return 'Selecione um equipamento.';
    if (selectedItems.length === 0) return 'Selecione ao menos um defeito.';
    if (!visitWindow) return 'Selecione um horário de visita.';
    if (hasRestriction === true && restrictionDescription.trim() === '') {
      return 'Descreva a restrição de horário de almoço.';
    }
    if (referencePoint.trim() === '') return 'Informe o ponto de referência.';
    return null;
  };

  const extractError = (error: any) => {
    const data = error?.response?.data;
    if (data?.errors) {
      const first: any = Object.values(data.errors)[0];
      Alert.alert('Erro de validação', Array.isArray(first) ? first[0] : String(first));
      return;
    }
    if (data?.message) {
      Alert.alert('Erro', data.message);
      return;
    }
    Alert.alert('Erro', 'Não foi possível registrar a solicitação.');
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      Alert.alert('Atenção', validationError);
      return;
    }
    const request: IMaintenanceRequest = {
      gebra: selectedGebra,
      items: selectedItems,
      visit_window: visitWindow,
      lunch_restriction: hasRestriction === true,
      reference_point: referencePoint.trim(),
    };
    if (hasRestriction === true) {
      request.lunch_restriction_desc = restrictionDescription.trim();
    }
    try {
      setSubmiting(true);
      const response = await EquipamentsService.RequestMaintenance(request);
      setResult(response.data);
    } catch (error) {
      console.error(error);
      extractError(error);
    } finally {
      setSubmiting(false);
    }
  };

  const resetForm = () => {
    setSelectedGebra('');
    setSelectedItems([]);
    setVisitWindow('');
    setHasRestriction(null);
    setRestrictionDescription('');
    setReferencePoint('');
    setResult(null);
  };

  if (loading) {
    return (
      <View style={[ms.container, {justifyContent: 'center'}]}>
        <ActivityIndicator color={Colors.RedPallete.red} size="large" />
      </View>
    );
  }

  // Tela de sucesso: exibe forecast + cada protocolo de results[].
  if (result) {
    return (
      <View style={[styles.container, {marginBottom: 0}]}>
        <View style={{marginTop: 30}}>
          <SearchHeader onPress={() => goBack()} title="Manutenção de Equipamentos" />
        </View>
        <ScrollView>
          <Text style={[ms.orders, {marginTop: 20}]}>Solicitação registrada</Text>
          {!!result.forecast && (
            <Text style={ms.description}>{result.forecast}</Text>
          )}
          <View style={{marginTop: 10}}>
            {result.results?.map(r => (
              <View key={r.id ?? r.protocol} style={ms.itemContainer}>
                <Text style={ms.itemText}>Defeito: {r.description}</Text>
                <Text style={ms.itemText}>Protocolo: {r.protocol ?? '—'}</Text>
                <Text
                  style={[
                    ms.itemText,
                    {
                      color: r.success
                        ? Colors.GreenPallete.greenOne
                        : Colors.RedPallete.red,
                    },
                  ]}>
                  {r.success ? 'Aberto com sucesso' : `Falha: ${r.error ?? 'erro desconhecido'}`}
                </Text>
              </View>
            ))}
          </View>
          <View style={{marginTop: 30}}>
            <ButtonComponent title="Nova Solicitação" outlined onPress={resetForm} />
          </View>
          <View style={{marginTop: 12}}>
            <ButtonComponent title="Concluir" onPress={() => goBack()} />
          </View>
        </ScrollView>
      </View>
    );
  }

  // Sem equipamentos: bloqueia o fluxo.
  if (equipments.length === 0) {
    return (
      <View style={[styles.container, {marginBottom: 0}]}>
        <View style={{marginTop: 30}}>
          <SearchHeader onPress={() => goBack()} title="Manutenção de Equipamentos" />
        </View>
        <View style={ms.containerError}>
          <Text style={ms.errorText}>
            Você não possui equipamentos disponíveis para solicitar manutenção.
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, {marginBottom: 0}]}>
      <View style={{marginTop: 30}}>
        <SearchHeader onPress={() => goBack()} title="Manutenção de Equipamentos" />
        <Text style={ms.description}>
          Aqui você pode solicitar a manutenção de equipamentos de forma fácil e rápida!
        </Text>
      </View>
      <ScrollView showsVerticalScrollIndicator={false}>
        {/* 1. Equipamento (GEBRA) */}
        <Text style={ms.label}>
          Selecione o equipamento
          <Text style={ms.required}> *</Text>
        </Text>
        {[...equipments]
          // Sem GEBRA (EQUNR) vão para o fim da lista.
          .sort(
            (a, b) =>
              (hasGebra(b) ? 1 : 0) - (hasGebra(a) ? 1 : 0),
          )
          .map((eq, index) => {
            // Equipamento sem GEBRA: não selecionável, cinza e com aviso.
            if (!hasGebra(eq)) {
              return (
                <View
                  key={`no-gebra-${index}`}
                  style={[ms.itemContainer, ms.itemDisabled]}>
                  <Text style={[ms.itemText, ms.itemTextDisabled]}>
                    {eq.MAKTX}
                  </Text>
                  <Text style={ms.unavailableText}>
                    Esse equipamento não está disponível no momento para
                    solicitação de manutenção. Contate o seu vendedor.
                  </Text>
                </View>
              );
            }
            const selected = selectedGebra === eq.EQUNR;
            return (
              <TouchableOpacity
                key={`${eq.EQUNR}-${index}`}
                activeOpacity={0.7}
                onPress={() => setSelectedGebra(eq.EQUNR)}
                style={[
                  ms.itemContainer,
                  {
                    borderWidth: 1,
                    borderColor: selected ? Colors.RedPallete.red : 'transparent',
                  },
                ]}>
                <Text style={ms.itemText}>{eq.MAKTX}</Text>
                <Text style={ms.subItemText}>{eq.EQUNR}</Text>
              </TouchableOpacity>
            );
          })}

        {/* 2. Defeitos */}
        <Text style={ms.label}>
          Informe um ou mais defeitos
          <Text style={ms.required}> *</Text>
        </Text>
        <View style={{marginTop: 10}}>
          {defects.map(defect => (
            <CheckBox
              key={defect.item}
              onPress={() => handleDefectToggle(defect)}
              isCheck={selectedItems.includes(defect.item)}
              description={defect.description}
              alignSelf="left"
            />
          ))}
        </View>

        {/* 4. Horário da visita */}
        <Text style={ms.label}>
          Horário da visita
          <Text style={ms.required}> *</Text>
        </Text>
        <View style={{flexDirection: 'row', gap: 10}}>
          {visitWindows.map(window => (
            <View key={window} style={{flex: 1}}>
              <ButtonComponent
                onPress={() => setVisitWindow(window)}
                title={window}
                outlined={visitWindow !== window}
              />
            </View>
          ))}
        </View>

        {/* 5. Restrição de almoço */}
        <Text style={ms.label}>
          Possui restrição de atendimento durante o horário de almoço?
        </Text>
        <View style={{flexDirection: 'row', gap: 10}}>
          {HAS_RESTRICTION.map(option => (
            <View key={option.id} style={{flex: 1}}>
              <ButtonComponent
                onPress={() => handleRestrictionSelection(option)}
                title={option.description}
                outlined={hasRestriction !== option.value}
              />
            </View>
          ))}
        </View>
        {hasRestriction === true && (
          <>
            <Text style={ms.label}>
              Explique o motivo da restrição de atendimento
              <Text style={ms.required}> *</Text>
            </Text>
            <TextInput
              multiline
              textAlignVertical="top"
              numberOfLines={4}
              value={restrictionDescription}
              onChangeText={setRestrictionDescription}
              style={[ms.input, {height: 100}]}
            />
          </>
        )}

        {/* 6. Ponto de referência */}
        <Text style={ms.label}>
          Ponto de referência do local
          <Text style={ms.required}> *</Text>
        </Text>
        <TextInput
          multiline
          textAlignVertical="top"
          numberOfLines={4}
          value={referencePoint}
          onChangeText={setReferencePoint}
          maxLength={200}
          style={[ms.input, {height: 100}]}
        />
        <Text style={[ms.subItemText, {alignSelf: 'flex-end', marginTop: 4}]}>
          {referencePoint.length}/200
        </Text>

        {/* 7. Registrar */}
        <View style={{marginTop: 30, marginBottom: 40}}>
          {submiting ? (
            <ActivityIndicator size="large" color={Colors.RedPallete.red} />
          ) : (
            <ButtonComponent onPress={handleSubmit} title="Solicitar" />
          )}
        </View>
      </ScrollView>

      {/* Modal de triagem (funciona em mobile e web) */}
      <Modal
        visible={!!triageDefect}
        transparent
        animationType="fade"
        onRequestClose={handleTriageRecovered}>
        <View style={ms.modalOverlay}>
          <View style={ms.modalCard}>
            <Text style={ms.modalTitle}>
              Triagem — {triageDefect?.description}
            </Text>
            <Text style={ms.modalText}>
              Antes de abrir o chamado, verifique:{'\n\n'}
              • Está com energia elétrica no local?{'\n'}
              • O disjuntor elétrico está ligado?{'\n'}
              • O abastecimento da geladeira foi feito há menos de 12 horas?
              {'\n\n'}
              Seu equipamento voltou a funcionar normalmente?
            </Text>
            <View style={{flexDirection: 'row', gap: 10, marginTop: 20}}>
              <View style={{flex: 1}}>
                <ButtonComponent
                  title="Sim"
                  outlined
                  onPress={handleTriageRecovered}
                />
              </View>
              <View style={{flex: 1}}>
                <ButtonComponent title="Não" onPress={handleTriageNotRecovered} />
              </View>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

export default EquipmentMaintenance;
